import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import axios from 'axios';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Admin from './components/Admin';
import MapView from './components/MapView';
import LogView from './components/LogView';

// Global axios interceptor: auto-clear stale token on 401/403
axios.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401 || err.response?.status === 403) {
      // Only clear if we actually have a token stored (avoid loop on login page)
      if (localStorage.getItem('aurora_token')) {
        localStorage.removeItem('aurora_token');
        localStorage.removeItem('aurora_user');
        window.location.reload();
      }
    }
    return Promise.reject(err);
  }
);

const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  
  // If accessed via HTTPS or standard ports (80/443), assume reverse proxy - use same origin
  if (window.location.protocol === 'https:' || 
      window.location.port === '' || 
      window.location.port === '80' || 
      window.location.port === '443') {
    return '';  // Use relative URLs (same origin)
  }
  
  // Development: use explicit API port
  const apiPort = process.env.REACT_APP_API_PORT || '30017';
  return `http://${window.location.hostname}:${apiPort}`;
};

const API_URL = getApiUrl();

function AppContent() {
  const [token, setToken] = useState(localStorage.getItem('aurora_token'));
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('aurora_user');
    return saved ? JSON.parse(saved) : null;
  });
  const [socket, setSocket] = useState(null);
  const [eventType, setEventType] = useState('WFD');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!token) return;
    
    let isMounted = true;
    let disconnectTimer = null;
    
    const newSocket = io(API_URL, {
      auth: { token },
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      timeout: 20000
    });
    
    newSocket.on('connect', () => {
      if (isMounted) {
        console.log('Socket connected:', newSocket.id);
        if (disconnectTimer) {
          clearTimeout(disconnectTimer);
          disconnectTimer = null;
        }
      }
    });
    
    newSocket.on('disconnect', (reason) => {
      if (isMounted) {
        console.log('Socket disconnected:', reason);
        disconnectTimer = setTimeout(() => {
          console.log('Socket disconnected for 1 minute, refreshing page...');
          window.location.reload();
        }, 60000);
      }
    });
    
    newSocket.on('connect_error', (err) => {
      if (isMounted) {
        console.log('Socket connection error:', err.message);
      }
    });
    
    if (isMounted) {
      setSocket(newSocket);
    }

    return () => {
      isMounted = false;
      if (disconnectTimer) {
        clearTimeout(disconnectTimer);
      }
      newSocket.close();
    };
  }, [token]);

  // Fetch event_type setting whenever token changes (i.e. after login)
  const fetchEventType = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/api/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.event_type) {
        setEventType(response.data.event_type);
      }
    } catch (err) {
      console.error('Error fetching event type:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchEventType();
  }, [fetchEventType]);

  const handleLogin = useCallback((authData) => {
    setToken(authData.token);
    setUser({ callsign: authData.callsign, isAdmin: authData.isAdmin });
    localStorage.setItem('aurora_token', authData.token);
    localStorage.setItem('aurora_user', JSON.stringify({
      callsign: authData.callsign,
      isAdmin: authData.isAdmin
    }));
  }, []);

  const handleLogout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('aurora_token');
    localStorage.removeItem('aurora_user');
    if (socket) {
      socket.close();
      setSocket(null);
    }
    navigate('/');
  }, [socket, navigate]);

  if (!token || !user) {
    return <Login onLogin={handleLogin} apiUrl={API_URL} />;
  }

  const isActive = (path) => location.pathname === path;

  return (
    <div className="app-container">
      <header className="header">
        <div className="logo">Aurora</div>
        <div className="user-info">
          <button
            className={`header-btn ${isActive('/') ? 'active' : ''}`}
            onClick={() => navigate('/')}
          >
            Entry
          </button>
          <button
            className={`header-btn ${isActive('/log') ? 'active' : ''}`}
            onClick={() => navigate('/log')}
          >
            Log
          </button>
          <button
            className={`header-btn ${isActive('/map') ? 'active' : ''}`}
            onClick={() => navigate('/map')}
          >
            Map
          </button>
          <button 
            className={`header-btn ${isActive('/objectives') ? 'active' : ''}`}
            onClick={() => navigate('/objectives')}
          >
            {eventType === 'ARFD' ? 'Bonuses' : 'Objectives'}
          </button>
          {user.isAdmin && (
            <button
              className={`header-btn ${isActive('/admin') ? 'active' : ''}`}
              onClick={() => navigate('/admin')}
            >
              Admin
            </button>
          )}
          <span className="callsign-badge">{user.callsign}</span>
          <button className="header-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      <Routes>
        <Route path="/" element={
          <Dashboard 
            token={token} 
            user={user} 
            socket={socket} 
            apiUrl={API_URL}
            eventType={eventType}
            onEventTypeChange={setEventType}
            showObjectives={false}
            onCloseObjectives={() => {}}
          />
        } />
        <Route path="/map" element={
          <MapView token={token} socket={socket} apiUrl={API_URL} />
        } />
        <Route path="/log" element={
          <LogView token={token} socket={socket} apiUrl={API_URL} user={user} eventType={eventType} />
        } />
        <Route path="/admin" element={
          user.isAdmin 
            ? <Admin token={token} apiUrl={API_URL} onSettingsChange={(s) => { if (s.event_type) setEventType(s.event_type); }} />
            : <Navigate to="/" />
        } />
        <Route path="/objectives" element={
          <Dashboard 
            token={token} 
            user={user} 
            socket={socket} 
            apiUrl={API_URL}
            eventType={eventType}
            onEventTypeChange={setEventType}
            showObjectives={true}
            onCloseObjectives={() => navigate('/')}
          />
        } />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
